import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { toFixed } from '@/utils/number'
import { useCoinBasis } from './coin'
import BigNumber from 'bignumber.js'
import fee from '@/utils/fee'
import i18n, { useTranslation } from '@/services/i18n'
import {
  FeeRateDto,
  GetFeeLevelDTO,
  NonceLimitResult,
  FeeLevelModel,
  TXInitDataModel,
  CheckAddrDTO,
  BaseAddressAndBalanceModel,
} from '@/services/apis/tx/models'
import { LOOP_FEE_INTERVAL } from '@/constants'
import { RuleItem, validate } from '@/utils/validator'
import { DestinationTypeEnum, TradeToAccountTypeEnum } from '@/constants/enum'
import txApi from '@/services/apis/tx'
import { getPrecison } from '@/utils/utils'
import { formatUSD } from '@/utils/format'

export const useEstimateFee = (coinKey: string) => {
  const coinBasis = useCoinBasis(coinKey)
  const feeCoinBasis = useCoinBasis(coinBasis?.feeCoinKey)

  const [feeExpression, setFeeExpression] = useState('')

  const getFeeExpresion = async (walletKey: string): Promise<boolean> => {
    const res = await txApi.getFeeExpression({
      accountKey: walletKey,
      coinKey,
    })
    if (res.success) {
      setFeeExpression(res.data.expression)
    }
    return res.success
  }
  // Return estimatedFee with symbol and usd amount
  // Allows "realFeeRateDto" values to be arbitrary strings, and when the value is arbitrary,
  // returns the result of the previous calculation
  const estimateFee = useCallback(
    (realFeeRateDto?: FeeRateDto, expression?: string): [string, string] => {
      if (!realFeeRateDto || (!expression && !feeExpression)) {
        return ['', '']
      }
      const estimatedFee = fee.estimateFee(realFeeRateDto, expression ?? feeExpression)
      const valWithTailZero = toFixed(
        estimatedFee,
        feeCoinBasis?.showCoinDecimal ?? 0,
        BigNumber.ROUND_HALF_UP
      )
      const valWithoutTailZero = new BigNumber(valWithTailZero).toFixed()
      return [valWithoutTailZero, feeCoinBasis?.symbol ?? '']
    },
    [coinBasis, feeExpression, feeCoinBasis]
  )
  const getFeeRateText = useCallback(
    (feeRate?: FeeRateDto) => {
      if (!feeRate) {
        return ''
      }
      switch (coinBasis?.coinImplTag) {
        case 'tron':
          return ''
        case 'utxo':
          return `${i18n.t('wallet.sendDrawer.gasPrice')} ${toFixed(
            feeRate?.feeRate,
            coinBasis?.feeDecimal ?? 0
          )} ${coinBasis?.feeUnit}`
        case 'eth':
          if (coinBasis?.feeType === 1) {
            return `MaxFee ${toFixed(feeRate?.maxFee, coinBasis?.feeDecimal ?? 0)} ${
              coinBasis?.feeUnit
            }`
          } else {
            return `${toFixed(feeRate?.feeRate, coinBasis?.feeDecimal ?? 0)} ${coinBasis?.feeUnit}`
          }
        default:
          return ''
      }
    },
    [coinBasis]
  )
  return { getFeeExpresion, feeExpression, estimateFee, getFeeRateText }
}

export const useLoopedFeeLevel = (dto: GetFeeLevelDTO) => {
  const [refreshing, setRefresing] = useState(false)
  const [countDown, setCountDown] = useState(LOOP_FEE_INTERVAL)
  const [feeLevelModel, setFeeLevelModel] = useState<FeeLevelModel | undefined>()
  let timer = useRef<any>()

  // 立即获取feeLevel
  useEffect(() => {
    if (!dto.coinKey || !dto.sourceAddress || !dto.destinationAddress) {
      return
    }
    if (refreshing) {
      return
    }
    setRefresing(true)
    txApi
      .getFeeLevel(dto)
      .then(res => {
        if (res.success) {
          setFeeLevelModel(res.data)
        }
      })
      .finally(() => {
        setRefresing(false)
      })
  }, [dto.coinKey, dto.sourceAddress, dto.destinationAddress])

  // 添加定时器
  useEffect(() => {
    if (!dto.coinKey) {
      return
    }
    timer.current = setInterval(() => {
      if (refreshing) {
        return
      }
      if (countDown > 1) {
        setCountDown(countDown - 1)
        return
      }
      setRefresing(true)
      txApi
        .getFeeLevel(dto)
        .then(res => {
          if (res.success) {
            setFeeLevelModel(res.data)
          }
        })
        .finally(() => {
          setRefresing(false)
          setCountDown(LOOP_FEE_INTERVAL)
        })
    }, 1000)
    return () => clearInterval(timer.current)
  }, [dto, countDown])

  return {
    feeLevelModel,
    countDown,
    timer,
  }
}

export const useValidateNonce = (
  value: string,
  nonceLimit: NonceLimitResult,
  nonceAlias = 'Nonce'
) => {
  const { t } = useTranslation()
  const validateRules = useMemo<RuleItem[]>(() => {
    const rules: RuleItem[] = [
      { validator: /^\d+$/, message: t('wallet.sendDrawer.nonceMustNum', { x: nonceAlias }) },
    ]

    if (nonceLimit) {
      if (
        nonceLimit.maxUseNonce === null ||
        nonceLimit.maxUseNonce === undefined ||
        nonceLimit.maxUseNonce + 1 <= nonceLimit.chainNonce
      ) {
        rules.push({
          validator: (text: string) => Number(text) >= Number(nonceLimit.chainNonce),
          message: t('wallet.sendDrawer.nonceTooLow', { x: nonceAlias }),
        })
        rules.push({
          validator: (text: string) => Number(text) <= Number(nonceLimit.chainNonce),
          message: t('wallet.sendDrawer.nonceTooHight', {
            x: nonceAlias,
            nonce: nonceLimit.chainNonce,
          }),
        })
      } else {
        rules.push({
          validator: (text: string) => Number(text) >= Number(nonceLimit.chainNonce),
          message: t('wallet.sendDrawer.nonceTooLow', { x: nonceAlias }),
        })

        rules.push({
          validator: (text: string) => Number(text) <= Number(nonceLimit.maxUseNonce) + 1,
          message: t('wallet.sendDrawer.nonceTooHight', {
            x: nonceAlias,
            nonce: Number(nonceLimit.maxUseNonce) + 1,
          }),
        })
      }
    }

    return rules
  }, [nonceLimit])
  return useMemo(() => {
    if (!value) {
      return {
        isValid: true,
        errMsg: '',
      }
    }
    return validate(value, validateRules)
  }, [value, validateRules])
}

export const useGetNonceLimit = (coinKey: string, address: string) => {
  const [nonce, setNonce] = useState('')
  const [nonceLimit, setNonceLimit] = useState<NonceLimitResult>()
  useEffect(() => {
    txApi.getNonceLimit({ coinKey, address }).then(res => {
      if (res.success) {
        setNonceLimit(res.data)
        if (res.data.maxUseNonce === null || res.data.maxUseNonce === undefined) {
          setNonce(`${res.data.chainNonce}`)
        } else {
          setNonce(`${res.data.maxUseNonce + 1}`)
        }
      }
    })
  }, [])
  return {
    nonceLimit,
    setNonce,
    nonce,
  }
}

export const useGetTxInitData = (
  coinKey: string,
  walletKey: string,
  destinationAddress: string
) => {
  const [txInitData, setTxInitData] = useState<TXInitDataModel>()
  useEffect(() => {
    if (!coinKey || !walletKey) {
      return
    }
    txApi
      .getTxInitData({
        coinKey,
        accountKey: walletKey,
        destinationAddress,
      })
      .then(res => {
        if (res.success) {
          setTxInitData(res.data)
        }
      })
  }, [coinKey, walletKey, destinationAddress])
  return txInitData
}

export interface ReceiverInfo {
  realName: string
  realAddr: string
  realDestinationId: string
  isContract: boolean
  isPhishing: boolean
  realDestionType: DestinationTypeEnum
  tradeDestinationType: TradeToAccountTypeEnum
}

export const useReceiverInfo = () => {
  const [isChecking, setIsChecking] = useState(false)
  const [receiverInfo, setReceiverInfo] = useState<ReceiverInfo>()

  const checkAndSetReceiver = async (
    dto: CheckAddrDTO
  ): Promise<{ isValid: boolean; errMsg?: string; isContract?: boolean; isPhishing?: boolean }> => {
    setIsChecking(true)
    const res = await txApi.checkAddr(dto)
    setIsChecking(false)
    if (res.success) {
      const data = res.data
      if (data?.isAddressValid === false) {
        return {
          isValid: false,
          errMsg: i18n.t('wallet.sendDrawer.addressFormatError') || '',
        }
      }
      if (data?.isAml === true) {
        return {
          isValid: false,
          errMsg: i18n.t('wallet.sendDrawer.amlTipMessage') || '',
        }
      }

      let realName = ''
      let realAddr = ''
      let realDestinationId = ''
      let tradeDestinationType = TradeToAccountTypeEnum.ANY

      let isContract = res.data.isContract ?? false
      let realDestionType = res.data.realDestinationType
      let isPhishing = res.data.isPhishing ?? false
      switch (res.data.realDestinationType) {
        case DestinationTypeEnum.VAULT_ACCOUNT:
          realName = res.data.vaultAccount.accountName
          realAddr = res.data.vaultAccount.address
          realDestinationId = res.data.vaultAccount.accountKey
          tradeDestinationType = TradeToAccountTypeEnum.VAULT_ACCOUNT
          break
        case DestinationTypeEnum.INTERNAL_WALLET:
          realName = res.data.addressBook.name
          realAddr = res.data.addressBook.address
          realDestinationId = res.data.addressBook.addressBookKey
          tradeDestinationType =
            res.data.addressBook.type === 1
              ? TradeToAccountTypeEnum.ONE_TIME_ADDRESS
              : TradeToAccountTypeEnum.WHITE_LIST
          break
        case DestinationTypeEnum.ONE_TIME_ADDRESS:
          realName = ''
          realAddr = res.data.oneAddress.address
          tradeDestinationType = TradeToAccountTypeEnum.ONE_TIME_ADDRESS
          break
        default:
          break
      }

      setReceiverInfo({
        realName,
        realAddr,
        realDestinationId,
        tradeDestinationType,
        isContract,
        realDestionType,
        isPhishing,
      })

      return {
        isValid: true,
        isContract: data?.isContract ?? false,
        isPhishing: data?.isPhishing ?? false,
      }
    }

    return {
      isValid: false,
    }
  }
  return { receiverInfo, checkAndSetReceiver, isChecking, setReceiverInfo }
}

export const useCalcUsdValue = (
  amount?: string | number,
  exchangeRateUsd?: number,
  precision = 2
) => {
  return useMemo(() => {
    if (amount && exchangeRateUsd) {
      return formatUSD(
        new BigNumber(amount).multipliedBy(new BigNumber(exchangeRateUsd)).toNumber(),
        precision
      )
    }
    return '0.00'
  }, [amount, exchangeRateUsd, precision])
}

export interface ValidatedAmountData {
  coinKey: string
  estimatedFee?: [string, string]
  accountInfo?: BaseAddressAndBalanceModel
  value: string
  onChange: (val: string) => void
}

export const useValidateSendAmount = (dto: ValidatedAmountData) => {
  const { coinKey, accountInfo, estimatedFee, onChange, value } = dto
  const coinBasis = useCoinBasis(coinKey)
  const precision = coinBasis?.showCoinDecimal ?? 0
  // 处理过精度的余额
  const fixedBalance = useMemo(
    () => toFixed(accountInfo?.coinBalance, precision ?? 0),
    [accountInfo, precision]
  )
  // 最小转账金额
  const minSendVal = useMemo<string>(() => {
    if (['utxo', 'fil', 'tron'].includes(coinBasis?.coinImplTag || '')) {
      return '0.00001'
    } else {
      return '0'
    }
  }, [coinKey, coinBasis, precision])

  // 转账可用的最大余额
  // 如果手续费币种和转账币种是同一个币种，则为 balance - fee
  // 如果不是同一个币种，则为 balance
  const maxSendVal = useMemo(() => {
    const feeAmount = (estimatedFee ? estimatedFee[0] : '0') || '0'
    if (coinBasis?.coinKey === accountInfo?.feeCoinKey) {
      return toFixed(
        new BigNumber(fixedBalance).minus(new BigNumber(feeAmount)).toFixed(),
        precision ?? 0,
        BigNumber.ROUND_DOWN
      )
    }
    return fixedBalance
  }, [estimatedFee, fixedBalance, coinBasis])

  // 校验规则
  const rules: RuleItem[] = useMemo<RuleItem[]>(() => {
    const res: RuleItem[] = []

    res.push({
      validator: (text: string) =>
        new BigNumber(text).isGreaterThanOrEqualTo(new BigNumber(minSendVal)),
      message: i18n.t('wallet.sendDrawer.miniSendNum', { num: minSendVal }),
    })

    res.push({
      validator: (text: string) =>
        new BigNumber(text).isLessThanOrEqualTo(new BigNumber(maxSendVal)),
      message: i18n.t('wallet.sendDrawer.insufficientBalance'),
    })

    res.push({
      validator: (text: string) => getPrecison(text) <= precision,
      message: i18n.t('common.validate.decimal', { decimal: precision }),
    })

    return res
  }, [accountInfo, minSendVal, maxSendVal, precision])

  const { errMsg } = useMemo(() => {
    const { errMsg } = validate(value, rules)
    return {
      errMsg,
    }
  }, [value, rules])

  const precisionRegExp = useMemo(
    () => new RegExp(`^(\\d+)\\.(\\d{0,${precision}}).*$`),
    [precision]
  )
  const handleChangeText = (text: string) => {
    if (text === '.') {
      // . 自动转化为 0.
      const v = '0.'
      onChange(v)
    } else {
      let newText = text
        .replace(/[^\d.]/g, '') // 移除数字和0以外的字符
        .replace(/\.{2,}/g, '.') // 移除连续多个点的后面的点
        .replace('.', '$#$') // 移除不连续的多个点的后面的点
        .replace(/\./g, '')
        .replace('$#$', '.')
        .replace(precisionRegExp, '$1.$2') // 允许输入指定精度的数字
      onChange(newText)
    }
  }

  const sendAll = () => {
    const maxValue =
      new BigNumber(maxSendVal).isLessThanOrEqualTo(0) || maxSendVal === 'NaN' ? '0' : maxSendVal
    handleChangeText(maxValue)
  }

  return {
    errMsg,
    sendAll,
    handleChangeText,
  }
}
